import React, { Component } from 'react';
import { shape, bool, arrayOf, string, func } from 'prop-types';
import { gql, graphql, compose } from 'react-apollo';
import { connect } from 'react-redux';

import { operations as routeCreationOperations } from 'state/ducks/routeCreation';
import { operations as mapOperations } from 'state/ducks/map';

import locationShape from 'propTypes/locationShape';
import coord from 'propTypes/coord';

import filterSegmentsByFrom from 'helpers/filterSegmentsByFrom';

import SegmentNameDisplay from 'components/SegmentNameDisplay';
import LoadingIndicator from 'components/LoadingIndicator';
import { LiveMapQuery } from 'components/Map/Live';

import Header from './Header';
import List from '../List';
import ListItem from '../ListItem';
import Footer from '../Footer';
import Title from '../Title';

import styles from './CreateRoute.scss';

const API_ERROR = '__error__';

const CreateRouteQuery = gql`
  query Segments {
    segments {
      id
      name
      status
      from {
        id
        name
        trackingDevice {
          id
          status
        }
      }
      to {
        id
        name
        trackingDevice {
          id
          status
        }
      }
      path
    }
  }
`;

const CreateRouteMutation = gql`
  mutation createRoute($segmentIds: [ID!]!) {
    createRoute(segmentIds: $segmentIds) {
      id
    }
  }
`;

class CreateRoute extends Component {
  state = {
    waitForResponse: false,
  };

  handleDone = () => {
    const { createRoute, setCreatedRouteId } = this.props;
    createRoute()
      .then(result => {
        setCreatedRouteId(result.data.createRoute.id);
        this.setState({ waitForResponse: false });
      })
      .catch(err => {
        this.setState({ waitForResponse: false });
        throw new Error(`Failed to create route: ${err.message}`);
      });

    this.setState({ waitForResponse: true });
  };

  handleClose = () => {
    this.props.finishCreateRoute();
  };

  handleCancel = () => {
    this.props.finishCreateRoute();
  };

  handleNew = () => {
    this.props.clearCreateRoute();
  };
  render() {
    const {
      data: { loading, segments },
      currentHover,
      enterEntity,
      leaveEntity,
      selectSegment,
      selectedSegments,
      createdRouteId,
    } = this.props;

    if (loading) return <LoadingIndicator />;

    const { waitForResponse } = this.state;

    const hasError = createdRouteId === API_ERROR;
    const availableSegments = filterSegmentsByFrom(segments, selectedSegments);

    const isFinalStep = selectedSegments && selectedSegments.length > 1;
    const isFinished = Boolean(createdRouteId);

    // Show the list if to/from selection is not complete
    const showList = availableSegments.length > 0 && !isFinished;
    const selectedSegmentsObjects = selectedSegments.reduce((acc, selectedSegmentId) => {
      const { from, to, id, status } = segments.find(segment => segment.id === selectedSegmentId);

      return [...acc, { from, to, id, status }];
    }, []);

    return (
      <div className={styles.main}>
        <div className={styles.top}>
          <Title>Create new route</Title>
          <Header selectedSegments={selectedSegmentsObjects} isFinished={isFinished} />
          {selectedSegments.length > 0 &&
            showList &&
            <h3 className={styles.subtitle}>Next available segments</h3>}
        </div>
        <div className={styles.content}>
          {showList &&
            <List>
              {availableSegments.map(segment => {
                const {
                  from: { name: fromName },
                  to: { name: toName },
                  status,
                  id: segmentId,
                } = segment;

                const isActive = currentHover && currentHover.id === segment.id;

                return (
                  <ListItem
                    key={`Segment-${segmentId}`}
                    id={segmentId}
                    contentType="segment"
                    isActive={isActive}
                    onMouseOver={() => enterEntity({ id: segmentId, type: 'segment' })}
                    onMouseOut={() => leaveEntity({ id: segmentId, type: 'segment' })}
                    onClick={() => selectSegment(segmentId)}
                  >
                    <div className={styles.segmentListItem}>
                      <SegmentNameDisplay
                        className={styles.segment}
                        fromLocationName={fromName}
                        toLocationName={toName}
                        status={status}
                      />
                    </div>
                  </ListItem>
                );
              })}
            </List>}

          {isFinished
            ? hasError
              ? <div className={styles.text}>An error occurred. This has been reported.</div>
              : <div className={styles.text}>You have successfully created a new route.</div>
            : null}
        </div>
        <Footer
          className={styles.footer}
          onCancel={this.handleCancel}
          onDone={this.handleDone}
          onNew={this.handleNew}
          onClose={this.handleClose}
          isFinalStep={isFinalStep}
          isFinished={isFinished}
          waitForResponse={waitForResponse}
        />
      </div>
    );
  }
}

CreateRoute.propTypes = {
  data: shape({
    loading: bool.isRequired,
    location: shape({}),
    segments: arrayOf(
      shape({
        id: string,
        path: arrayOf(coord),
        from: locationShape,
        to: locationShape,
      })
    ),
  }).isRequired,
  selectSegment: func.isRequired,
  createRoute: func.isRequired,
  setCreatedRouteId: func.isRequired,
  clearCreateRoute: func.isRequired,
  finishCreateRoute: func.isRequired,
  enterEntity: func.isRequired,
  leaveEntity: func.isRequired,
  selectedSegments: arrayOf(string).isRequired,
  createdRouteId: string,
  currentHover: shape({
    id: string,
  }),
};

const mapStateToProps = ({ map, routeCreation }) => ({
  currentHover: map.currentHover,
  selectedSegments: routeCreation.selectedSegments,
  createdRouteId: routeCreation.createdRouteId,
});

const ConnectedCreateRouteWithData = compose(
  connect(mapStateToProps, { ...mapOperations, ...routeCreationOperations }),
  graphql(CreateRouteQuery, {}),
  graphql(CreateRouteMutation, {
    props: ({ mutate, ownProps: { selectedSegments } }) => ({
      createRoute: () =>
        mutate({
          variables: {
            segmentIds: selectedSegments,
          },
          refetchQueries: [
            {
              query: LiveMapQuery,
            },
          ],
        }),
    }),
  })
)(CreateRoute);

export default ConnectedCreateRouteWithData;



// WEBPACK FOOTER //
// ./src/components/MapPanel/CreateRoute/index.js